/**
 * PriorityPill - the queue-rank axis next to the ScoreDeltaChip's
 * score-lift axis, drawn as a Carbon severity: the glyph's shape, then the
 * word (the Shape-First Severity Rule). Only critical takes a hue.
 *
 * The words are the plain severities (D33: "critical" in UI copy, never
 * SHALL). BPA rows keep their framework-tier explanation in the tooltip:
 * the 1:1 mapping crit↔SHALL / high↔SHOULD / low↔MAY is enforced in
 * BpaActionMapper.MapSeverity, so deriving it on the client is safe. Drift
 * and identity rows get the generic explanation, because their severities
 * aren't framework prescriptions.
 *
 * The tooltip describes the glyph and never becomes a control: the pill sits
 * inside clickable queue rows.
 */

import type { Pillar, Severity } from '@/lib/posture-brief'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { Tooltip } from '@/components/carbon/Tooltip'
import type { SeverityLevel } from '@/components/carbon/types'

const LEVEL: Record<Severity, SeverityLevel> = {
  crit: 'crit',
  high: 'high',
  med: 'med',
  low: 'low',
  routine: 'low',
}

const WORD: Record<Severity, string> = {
  crit: 'Critical',
  high: 'High',
  med: 'Medium',
  low: 'Low',
  routine: 'Routine',
}

const BPA_TOOLTIP: Partial<Record<Severity, string>> = {
  crit: "Must-fix per the frameworks. That's why it ranks high. The points tag beside it is separate: it's the score lift you actually get from closing this.",
  high: "Recommended by the frameworks. Ranks below the must-fix items. The points tag shows the score lift you'd get from closing this.",
  low:  "Optional or aspirational. Sits at the bottom of the queue. The points tag shows the score lift you'd get from closing this.",
}

const GENERIC_TOOLTIP: Record<Severity, string> = {
  crit:    'Top priority. Fix this first.',
  high:    'High priority. Worth fixing soon.',
  med:     'Medium priority.',
  low:     'Low priority.',
  routine: 'Minor finding. Ranked by tie-breaks.',
}

export interface PriorityPillProps {
  severity: Severity
  pillar: Pillar
}

export default function PriorityPill({ severity, pillar }: PriorityPillProps) {
  // The framework-tier explanation only when the pillar is BPA AND the
  // severity maps to a tier; med / routine fall back to the generic copy.
  const tooltip = (pillar === 'bpa' && BPA_TOOLTIP[severity]) || GENERIC_TOOLTIP[severity]

  return (
    <Tooltip interactive={false} label={tooltip} align="start">
      <SeverityGlyph level={LEVEL[severity]}>{WORD[severity]}</SeverityGlyph>
    </Tooltip>
  )
}
